import {
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_MARKETPLACE_PARTNER_THUMBNAIL,
} from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import {
  putToSignedUrlWithProgress,
  stageMemoryPreflightUpload,
  type UploadConfirmResult,
  type UploadPresignResult,
} from "@/features/preflight/api/uploads.api";
import { resolvePartnerPreflightContentType } from "@/features/marketplace/lib/resolve-partner-preflight-content-type";

const THUMBNAIL_POLICY = UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_PARTNER_THUMBNAIL];

async function presignPartnerThumbnail(input: {
  tenantId: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
  signal?: AbortSignal;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    signal: input.signal,
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: UPLOAD_PURPOSE_MARKETPLACE_PARTNER_THUMBNAIL,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

async function confirmPartnerThumbnail(input: {
  tenantId: string;
  uploadId: string;
  signal?: AbortSignal;
}): Promise<UploadConfirmResult> {
  const { data } = await authFetch<UploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    signal: input.signal,
    body: JSON.stringify({
      tenantId: input.tenantId,
      uploadId: input.uploadId,
    }),
  });
  return data;
}

/** presign → upload (or stage) → confirm for one partner thumbnail image. */
export async function runPartnerThumbnailUpload(input: {
  tenantId: string;
  file: File;
  signal?: AbortSignal;
}): Promise<UploadConfirmResult> {
  const contentType = resolvePartnerPreflightContentType(input.file, THUMBNAIL_POLICY);
  const presign = await presignPartnerThumbnail({
    tenantId: input.tenantId,
    contentType,
    sizeBytes: input.file.size,
    originalName: input.file.name,
    signal: input.signal,
  });

  if (presign.requiresMemoryStage || presign.requiresServerStage) {
    await stageMemoryPreflightUpload({
      tenantId: input.tenantId,
      uploadId: presign.uploadId,
      contentType,
      file: input.file,
      signal: input.signal,
    });
  } else {
    await putToSignedUrlWithProgress(presign.uploadUrl, input.file, presign.requiredHeaders, {
      signal: input.signal,
    });
  }

  return confirmPartnerThumbnail({
    tenantId: input.tenantId,
    uploadId: presign.uploadId,
    signal: input.signal,
  });
}
